// ============================================
// Layout Manager — Screen & Overlay Controller
// Handles screen transitions, overlay stack,
// panel collapse, device detection
// ============================================

import { events, Events } from '../engine/events.js';

// Overlay close priority (ESC key — first match wins)
const ESC_PRIORITY = [
    'overlay-planner',
    'overlay-history',
    'overlay-journal',
    'overlay-phone',
    'overlay-bugreport',
    'overlay-feedback',
    'overlay-shop',
    'overlay-wardrobe',
    'overlay-achievement',
    'overlay-gallery',
    'overlay-pause',
];

class LayoutManager {
    constructor() {
        /** @type {string} Current active screen ID */
        this.currentScreen = 'screen-main-menu';

        /** @type {string[]} Stack of open overlay IDs */
        this.overlayStack = [];

        /** @type {'desktop'|'tablet'|'phone'} Device class */
        this.deviceClass = 'desktop';

        /** @type {boolean} Left panel collapsed */
        this.leftCollapsed = false;

        /** @type {boolean} Right panel collapsed */
        this.rightCollapsed = false;

        this._backdropEl = null;
        this._bound = false;
    }

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    init() {
        if (this._bound) return;
        this._bound = true;

        this._backdropEl = document.getElementById('overlay-backdrop');

        this._detectDevice();
        this._bindKeyboard();
        this._bindOverlayCloseButtons();
        this._bindBackdrop();
        this._bindPanelCollapse();
        this._restorePanelState();

        // Re-detect on resize (debounced)
        let _resizeTimer;
        const _resizeHandler = () => {
            clearTimeout(_resizeTimer);
            _resizeTimer = setTimeout(() => this._detectDevice(), 150);
        };
        window.addEventListener('resize', _resizeHandler);
        // Android WebView fires visualViewport.resize when system bars
        // toggle even if window.resize doesn't (BUG_PATTERNS § 76).
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', _resizeHandler);
        }

        // Listen for screen change requests
        this._scope = events.createScope('layout');
        this._scope.on(Events.SCREEN_CHANGE, (screenId) => this.showScreen(screenId));
        this._scope.on(Events.OVERLAY_OPEN, (overlayId) => this.openOverlay(overlayId));
        this._scope.on(Events.OVERLAY_CLOSE, (overlayId) => this.closeOverlay(overlayId));
        this._scope.on(Events.FORCED_EVENT_STARTED, () => this.closeAllOverlays());
    }

    /** Detach all event listeners (tests + future re-init paths). */
    destroy() {
        if (this._scope) this._scope.destroy();
    }

    // ------------------------------------------------------------------
    // Screen Management
    // ------------------------------------------------------------------

    /**
     * Switch to a screen with fade transition.
     * @param {string} screenId - e.g. 'screen-main-menu', 'screen-game-view'
     */
    showScreen(screenId) {
        const next = document.getElementById(screenId);
        if (!next || screenId === this.currentScreen) return;

        // Hide ALL screens immediately (prevents split-screen bug)
        document.querySelectorAll('.screen.active').forEach(s => {
            s.classList.remove('active', 'screen-transition-out');
        });

        // Show target screen
        next.classList.add('active');
        this.currentScreen = screenId;

        // DOM unmount (#19): once the player enters the game view, the main
        // menu and character editor are not needed anymore. Going back to
        // either uses location.reload() (see save-load-ui), so we can safely
        // detach their children to free DOM/memory. Saves ~150 nodes + any
        // preloaded images they were holding.
        if (screenId === 'screen-game-view') {
            for (const dropId of ['screen-main-menu', 'screen-character-editor']) {
                const drop = document.getElementById(dropId);
                if (drop && !drop.dataset.unmounted) {
                    while (drop.firstChild) drop.removeChild(drop.firstChild);
                    drop.dataset.unmounted = '1';
                }
            }
        }
    }

    // ------------------------------------------------------------------
    // Overlay Management
    // ------------------------------------------------------------------

    /**
     * Open an overlay with animation.
     * @param {string} overlayId - e.g. 'overlay-pause'
     * @param {boolean} playSound - emit sound event
     */
    openOverlay(overlayId, playSound = true) {
        const el = document.getElementById(overlayId);
        if (!el) return;

        // Cancel any pending close animation on this overlay
        if (el._closeTimer) { clearTimeout(el._closeTimer); el._closeTimer = null; }

        // Show backdrop
        if (this._backdropEl) {
            this._backdropEl.classList.remove('hidden');
        }

        // Remove hidden, trigger animation
        el.classList.remove('hidden', 'overlay-out');
        el.style.animation = 'none';
        el.offsetHeight; // force reflow
        el.style.animation = '';

        // Track in stack
        if (!this.overlayStack.includes(overlayId)) {
            this.overlayStack.push(overlayId);
        }

        if (playSound) {
            events.emit(Events.AUDIO_SFX_PLAY, 'pop1');
        }

        // Close on click outside .overlay-box (click on overlay backdrop area)
        if (!el._outsideClickBound) {
            el._outsideClickBound = true;
            el.addEventListener('click', (e) => {
                // Only close if clicking the overlay container itself, not its children
                if (e.target === el) {
                    this.closeOverlay(overlayId);
                }
            });
        }
    }

    /**
     * Close an overlay with animation.
     * @param {string} overlayId
     */
    closeOverlay(overlayId) {
        const el = document.getElementById(overlayId);
        if (!el || el.classList.contains('hidden')) return;

        // Remove from stack immediately (before animation)
        const idx = this.overlayStack.indexOf(overlayId);
        if (idx >= 0) this.overlayStack.splice(idx, 1);

        el.classList.add('overlay-out');

        // Cancel previous timer if rapid toggle
        if (el._closeTimer) clearTimeout(el._closeTimer);
        el._closeTimer = setTimeout(() => {
            // Only hide if still in closing state (not re-opened during animation)
            if (el.classList.contains('overlay-out')) {
                el.classList.add('hidden');
                el.classList.remove('overlay-out');
            }
            el._closeTimer = null;
        }, 250);

        events.emit(Events.AUDIO_SFX_PLAY, 'click_back1');
        // Notification (past tense, distinct from the OVERLAY_CLOSE request
        // this method is a listener FOR): every hide path funnels through
        // here, so modules with body-level satellites outside the overlay
        // element (gallery fullscreen viewer) can tear them down — hiding
        // #overlay-gallery alone left the viewer's unmuted <video controls>
        // playing on document.body (§ 91 family, audit 2026-06-07).
        events.emit(Events.OVERLAY_CLOSED, overlayId);

        // Hide backdrop if no overlays remain
        if (this.overlayStack.length === 0 && this._backdropEl) {
            this._backdropEl.classList.add('hidden');
        }
    }

    /**
     * Toggle an overlay: close if open, open if closed.
     */
    _toggleOverlay(overlayId) {
        const el = document.getElementById(overlayId);
        if (!el) return;
        if (el.classList.contains('hidden')) {
            this.openOverlay(overlayId);
        } else {
            this.closeOverlay(overlayId);
        }
    }

    /**
     * Close the topmost overlay in the ESC priority list.
     * @returns {boolean} true if an overlay was closed
     */
    closeTopOverlay() {
        for (const id of ESC_PRIORITY) {
            const el = document.getElementById(id);
            if (el && !el.classList.contains('hidden')) {
                this.closeOverlay(id);
                return true;
            }
        }
        return false;
    }

    /**
     * Close all open overlays.
     */
    closeAllOverlays() {
        for (const id of [...this.overlayStack]) {
            this.closeOverlay(id);
        }
    }

    /**
     * Check if a specific overlay is open.
     * @param {string} overlayId
     * @returns {boolean}
     */
    isOverlayOpen(overlayId) {
        const el = document.getElementById(overlayId);
        return el && !el.classList.contains('hidden');
    }

    // ------------------------------------------------------------------
    // Panel Collapse
    // ------------------------------------------------------------------

    toggleLeftPanel() {
        // In scene-mode, toggle user-expanded override instead of leftCollapsed,
        // so the user can re-expand a sidebar that scene-mode auto-collapsed.
        if (document.body.classList.contains('scene-mode')) {
            const left = document.getElementById('left-column');
            if (left) {
                const willExpand = !left.classList.contains('user-expanded');
                left.classList.toggle('user-expanded', willExpand);
                // A persisted desktop collapse (.collapsed / PanelCollapseLeft) keeps
                // the children display:none even when user-expanded, so the panel
                // opens EMPTY. Clear it when expanding so the sidebar fills and the
                // bad state doesn't survive restart (foundby:CutieLvr).
                if (willExpand && this.leftCollapsed) {
                    this.leftCollapsed = false;
                    left.classList.remove('collapsed');
                    this._persistPanelState();
                }
                // Persist so the panel stays open across scenes (foundby:CutieLvr).
                try { localStorage.setItem('PnO_SceneSidebarLeft', willExpand ? '1' : '0'); } catch (e) { /* ignore */ }
            }
            return;
        }
        this.leftCollapsed = !this.leftCollapsed;
        this._applyPanelState();
        this._persistPanelState();
    }

    toggleRightPanel() {
        if (document.body.classList.contains('scene-mode')) {
            const right = document.getElementById('right-column');
            if (right) {
                const willExpand = !right.classList.contains('user-expanded');
                right.classList.toggle('user-expanded', willExpand);
                // Mirror of toggleLeftPanel: a leftover .collapsed would blank the
                // re-opened panel's children (foundby:CutieLvr).
                if (willExpand && this.rightCollapsed) {
                    this.rightCollapsed = false;
                    right.classList.remove('collapsed');
                    this._persistPanelState();
                }
                try { localStorage.setItem('PnO_SceneSidebarRight', willExpand ? '1' : '0'); } catch (e) { /* ignore */ }
            }
            return;
        }
        this.rightCollapsed = !this.rightCollapsed;
        this._applyPanelState();
        this._persistPanelState();
    }

    /**
     * Open left panel as drawer (mobile).
     */
    openLeftDrawer() {
        const left = document.getElementById('left-column');
        const right = document.getElementById('right-column');
        if (!left) return;

        // Close right first
        if (right) right.classList.remove('drawer-open');
        this._removePhoneBackdrop();

        left.classList.add('drawer-open');
        document.getElementById('screen-game-view')?.classList.add('drawer-open');
        this._addPhoneBackdrop();
    }

    /**
     * Open right panel as drawer (mobile).
     */
    openRightDrawer() {
        const left = document.getElementById('left-column');
        const right = document.getElementById('right-column');
        if (!right) return;

        if (left) left.classList.remove('drawer-open');
        this._removePhoneBackdrop();

        right.classList.add('drawer-open');
        document.getElementById('screen-game-view')?.classList.add('drawer-open');
        this._addPhoneBackdrop();
    }

    closeDrawers() {
        const left = document.getElementById('left-column');
        const right = document.getElementById('right-column');
        if (left) left.classList.remove('drawer-open');
        if (right) right.classList.remove('drawer-open');
        document.getElementById('screen-game-view')?.classList.remove('drawer-open');
        this._removePhoneBackdrop();
    }

    // ------------------------------------------------------------------
    // Device Detection
    // ------------------------------------------------------------------

    _detectDevice() {
        const w = window.innerWidth;
        let newClass;

        if (w < 768) {
            newClass = 'phone';
        } else if (w < 1200) {
            newClass = 'tablet';
        } else {
            newClass = 'desktop';
        }

        if (newClass !== this.deviceClass) {
            document.body.classList.remove('desktop', 'tablet', 'phone', 'mobile');
            this.deviceClass = newClass;
            document.body.classList.add(newClass);

            if (newClass === 'phone' || newClass === 'tablet') {
                document.body.classList.add('mobile');
            }
        }

        // Update stats strip visibility
        this._updateStatsStripVisibility();
    }

    /** @returns {boolean} */
    get isMobile() { return this.deviceClass === 'phone' || this.deviceClass === 'tablet'; }
    /** @returns {boolean} */
    get isPhone() { return this.deviceClass === 'phone'; }

    // ------------------------------------------------------------------
    // Private: Key Bindings
    // ------------------------------------------------------------------

    _bindKeyboard() {
        document.addEventListener('keydown', (e) => {
            // ESC: close overlays
            if (e.key === 'Escape') {
                e.preventDefault();
                // Close drawer first on mobile
                const left = document.getElementById('left-column');
                const right = document.getElementById('right-column');
                if (left?.classList.contains('drawer-open') || right?.classList.contains('drawer-open')) {
                    this.closeDrawers();
                    return;
                }
                // P0: Computer mode — ESC triggers shutdown
                const computerOverlay = document.getElementById('overlay-computer');
                if (computerOverlay && !computerOverlay.classList.contains('hidden')) {
                    events.emit('computer:shutdown');
                    return;
                }

                this.closeTopOverlay();
                return;
            }

            // Image modal — arrow keys and F are handled by story-content.js
            const modal = document.getElementById('image-modal');
            if (modal && !modal.classList.contains('hidden')) {
                // Only close on Escape (handled by story-content.js); ignore other keys here
                return;
            }

            // Don't handle shortcuts if typing in an input/textarea
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            // Game screen shortcuts (only when game view is active)
            const gameView = document.getElementById('screen-game-view');
            if (!gameView || gameView.classList.contains('hidden')) return;

            switch (e.key.toLowerCase()) {
                case 'p':
                    // Phone overlay (main.js handles open with phoneUI.open())
                    e.preventDefault();
                    events.emit('shortcut:phone');
                    break;
                case 'j':
                    e.preventDefault();
                    this._toggleOverlay('overlay-journal');
                    break;
                case 'g':
                    // Gallery overlay (main.js handles open with galleryUI.open())
                    e.preventDefault();
                    events.emit('shortcut:gallery');
                    break;
                case 'h':
                    e.preventDefault();
                    this._toggleOverlay('overlay-history');
                    break;
                case 'i':
                    // Wardrobe/Inventory overlay (main.js handles open with wardrobeUI)
                    e.preventDefault();
                    events.emit('shortcut:wardrobe');
                    break;
                case 'm':
                    // Map/Location overlay (main.js handles toggle)
                    e.preventDefault();
                    events.emit('shortcut:map');
                    break;
                case 'tab':
                    e.preventDefault();
                    events.emit('shortcut:wardrobe');
                    break;
                case 'f11':
                    e.preventDefault();
                    if (document.fullscreenElement) {
                        document.exitFullscreen?.();
                    } else {
                        document.documentElement.requestFullscreen?.();
                    }
                    break;
                case 'arrowright': {
                    // Advance to next time slot (click next available time-seg)
                    const segs = document.querySelectorAll('.time-seg');
                    const active = document.querySelector('.time-seg.active');
                    if (active) {
                        const idx = parseInt(active.dataset.slot);
                        const next = document.querySelector(`.time-seg[data-slot="${idx + 1}"]`);
                        if (next) { e.preventDefault(); next.click(); }
                    }
                    break;
                }
            }
        });

        // Right-click to close overlays
        document.addEventListener('contextmenu', (e) => {
            if (this.overlayStack.length > 0) {
                e.preventDefault();
                this.closeTopOverlay();
            }
        });
    }

    _bindOverlayCloseButtons() {
        document.querySelectorAll('[data-close]').forEach(btn => {
            btn.addEventListener('click', () => {
                const overlayId = btn.dataset.close;
                this.closeOverlay(overlayId);
            });
        });
    }

    _bindBackdrop() {
        if (!this._backdropEl) return;
        this._backdropEl.addEventListener('click', () => {
            this.closeTopOverlay();
        });
    }

    _bindPanelCollapse() {
        const btnLeft = document.getElementById('btn-collapse-left');
        const btnRight = document.getElementById('btn-collapse-right');

        if (btnLeft) {
            btnLeft.addEventListener('click', () => {
                if (this.isPhone) {
                    this.closeDrawers();
                } else {
                    this.toggleLeftPanel();
                }
            });
        }

        if (btnRight) {
            btnRight.addEventListener('click', () => {
                if (this.isPhone) {
                    this.closeDrawers();
                } else {
                    this.toggleRightPanel();
                }
            });
        }

        // Mobile toggle buttons
        const btnToggleLeft = document.getElementById('btn-toggle-left');
        const btnToggleRight = document.getElementById('btn-toggle-right');

        if (btnToggleLeft) {
            btnToggleLeft.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this.isPhone) {
                    const left = document.getElementById('left-column');
                    if (left?.classList.contains('drawer-open')) {
                        this.closeDrawers();
                    } else {
                        this.openLeftDrawer();
                    }
                } else {
                    this.toggleLeftPanel();
                }
            });
        }

        if (btnToggleRight) {
            btnToggleRight.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this.isPhone) {
                    const right = document.getElementById('right-column');
                    if (right?.classList.contains('drawer-open')) {
                        this.closeDrawers();
                    } else {
                        this.openRightDrawer();
                    }
                } else {
                    this.toggleRightPanel();
                }
            });
        }
    }

    // ------------------------------------------------------------------
    // Private: Panel State
    // ------------------------------------------------------------------

    _applyPanelState() {
        const left = document.getElementById('left-column');
        const right = document.getElementById('right-column');
        const center = document.getElementById('center-panel');

        if (left) {
            left.classList.toggle('collapsed', this.leftCollapsed);
        }
        if (right) {
            right.classList.toggle('collapsed', this.rightCollapsed);
        }

        // Expanded mode for center
        if (center) {
            const bothCollapsed = this.leftCollapsed || this.rightCollapsed;
            center.classList.toggle('panels-expanded', bothCollapsed);
        }

        this._updateStatsStripVisibility();
    }

    _persistPanelState() {
        try {
            localStorage.setItem('PanelCollapseLeft', this.leftCollapsed ? '1' : '0');
            localStorage.setItem('PanelCollapseRight', this.rightCollapsed ? '1' : '0');
        } catch (e) { /* ignore */ }
    }

    _restorePanelState() {
        try {
            this.leftCollapsed = localStorage.getItem('PanelCollapseLeft') === '1';
            this.rightCollapsed = localStorage.getItem('PanelCollapseRight') === '1';
            this._applyPanelState();
        } catch (e) { /* ignore */ }
    }

    _updateStatsStripVisibility() {
        const strip = document.getElementById('stats-strip');
        if (!strip) return;

        // 0.2.9: the full stat bars moved behind the Stats tab in the combined
        // right panel, so the at-a-glance strip is now ALWAYS visible (it used to
        // show only when the left stat column was collapsed / on phone).
        strip.classList.add('visible');
    }

    // ------------------------------------------------------------------
    // Private: Phone Backdrop
    // ------------------------------------------------------------------

    _addPhoneBackdrop() {
        this._removePhoneBackdrop();
        const mainContent = document.querySelector('.main-content');
        if (!mainContent) return;

        const backdrop = document.createElement('div');
        backdrop.className = 'phone-backdrop';
        backdrop.addEventListener('click', () => this.closeDrawers());
        mainContent.appendChild(backdrop);
    }

    _removePhoneBackdrop() {
        const existing = document.querySelector('.phone-backdrop');
        if (existing) existing.remove();
    }
}

export const layoutManager = new LayoutManager();
